import { LoadErrorView } from "../LoadErrorView";
import { LiveblogCoreWidget } from "../liveblog-core/LiveblogCoreWidget";
import { EnvironmentProvider, ErrorBoundary } from "@webng/react-app-common";
import globalSettings from "../globalSettings";
import {LiveblogWidgetProps} from "./LiveblogWidgetProps";
import React, {ForwardedRef, forwardRef, useEffect, useMemo, useState} from "react";
import {useLiveblogWidgetData} from "./useLiveblogWidgetData";
import {LiveblogCoreApi} from "../liveblog-core/LiveblogCoreWidgetProps";
import {I18nWrapper} from "../I18nWrapper";
import {QueryClient, QueryClientProvider} from "@tanstack/react-query";
import {sharedThemeConfigurationSchema} from "@webng-types/embedjs/shared";
import {liveblogThemeConfigurationSchema} from "@webng-types/embedjs/liveblog";
import {removeSchemaOrgInjector, schemaOrgInjector} from "../../utils/schemaOrgInjector";
import { ChangeBridgeProvider } from "@webng/liveblog/src/change-stream/useChangeBridge";

interface LiveblogControllerProps extends LiveblogWidgetProps {

}

function LiveblogControllerImp({ clientId, themeId, liveblogId, eventId, initialData, customerConsent, overrideLiveblogThemeConfiguration, overrideSharedThemeConfiguration, disableTracking, relativeLinkBaseUrl }: LiveblogControllerProps, ref: ForwardedRef<LiveblogCoreApi>) {
  const { error, sharedThemeConfiguration, liveblogThemeConfiguration, configuration, schemaOrg, css, ...coreInitialData } = useLiveblogWidgetData({
    clientId,
    themeId,
    liveblogId,
    limit: overrideLiveblogThemeConfiguration.limit,
    sort: overrideLiveblogThemeConfiguration.sort,
    initialData
  })

  const [changeBridgeLiveblogLocalId, setChangeBridgeLiveblogLocalId] = useState<string | undefined>(undefined)

  // One client for the life of the controller. Constructing it inline gave every render a fresh
  // empty cache, so nothing under this provider could ever read a cached answer.
  const [queryClient] = useState(() => new QueryClient())

  const [finalLiveblogThemeConfiguration, finalSharedThemeConfiguration] = useMemo(() => {
    if (liveblogThemeConfiguration && sharedThemeConfiguration) {

      const sharedConfiguration = sharedThemeConfigurationSchema.parse(Object.assign({}, sharedThemeConfiguration, overrideSharedThemeConfiguration))
      const liveblogConfiguration = liveblogThemeConfigurationSchema.parse(Object.assign({}, liveblogThemeConfiguration, overrideLiveblogThemeConfiguration))

      return [liveblogConfiguration, sharedConfiguration]
    } else {
      return [undefined, undefined]
    }
  }, [liveblogThemeConfiguration, sharedThemeConfiguration, overrideLiveblogThemeConfiguration, overrideSharedThemeConfiguration])

  useEffect(() => {
    if(schemaOrg) {
      const id = `tickaroo-liveblog-schema-org-${liveblogId}`
      if(finalLiveblogThemeConfiguration?.injectSchemaOrg === 'auto') {
        schemaOrgInjector(id, schemaOrg, "LiveBlogPosting")
        return () => removeSchemaOrgInjector(id)
      } else if(finalLiveblogThemeConfiguration?.injectSchemaOrg === 'always') {
        schemaOrgInjector(id, schemaOrg, undefined)
        return () => removeSchemaOrgInjector(id)
      }
    }
  }, [liveblogId, schemaOrg, finalLiveblogThemeConfiguration?.injectSchemaOrg]);

  if (error !== undefined) {
    return <LoadErrorView status={error} />
  } else if (finalLiveblogThemeConfiguration && finalSharedThemeConfiguration) {
    return <I18nWrapper locale={finalSharedThemeConfiguration.locale} gameShowResponse={coreInitialData.gameShowResponse}>
      <QueryClientProvider client={queryClient}>
        <ChangeBridgeProvider liveblogLocalId={changeBridgeLiveblogLocalId} setLiveblogLocalId={setChangeBridgeLiveblogLocalId}>
        <LiveblogCoreWidget ref={ref}
                            initialData={coreInitialData || {}}
                            css={css || ""}
                            eventId={eventId}
                            liveblogId={liveblogId}
                            customerConsent={customerConsent}
                            disableTracking={disableTracking}
                            relativeLinkBaseUrl={relativeLinkBaseUrl}
                            liveblogThemeConfiguration={finalLiveblogThemeConfiguration}
                            sharedThemeConfiguration={finalSharedThemeConfiguration}
                            themeId={themeId}
        />
        </ChangeBridgeProvider>
      </QueryClientProvider>
    </I18nWrapper>
  } else {
    return <React.Fragment />
  }
}


const LiveblogController = forwardRef(LiveblogControllerImp)

function LiveblogWidgetImp({ clientId, ...props }: LiveblogWidgetProps, ref: ForwardedRef<LiveblogCoreApi>) {
  return <ErrorBoundary>
    <EnvironmentProvider apiHost={globalSettings.baseUrl} clientId={clientId}>
      <LiveblogController ref={ref} clientId={clientId} {...props} />
    </EnvironmentProvider>
  </ErrorBoundary>
}


export const LiveblogWidget = forwardRef(LiveblogWidgetImp)
